import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Modal,
  Form,
  Upload,
  message,
  Tag,
  Breadcrumb,
  Row,
  Col,
  Avatar,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  SearchOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { UserService, type UserWithRelations } from '../services/userService';
import { ExcelService, type UploadResult } from '../services/excelService';
import type { Database } from '../types/database.types';
import './UserManagement.css';
import { logger } from '../utils/logger';

const { Option } = Select;
const { confirm } = Modal;

type Company = Database['public']['Tables']['companies']['Row'];
type Branch = Database['public']['Tables']['branches']['Row'];

const UserManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithRelations | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserWithRelations[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [filteredBranches, setFilteredBranches] = useState<Branch[]>([]);
  const [filters, setFilters] = useState({
    companyId: undefined as string | undefined,
    branchId: undefined as string | undefined,
    isActive: undefined as boolean | undefined,
    searchTerm: undefined as string | undefined,
  });
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadResultModalVisible, setUploadResultModalVisible] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // 데이터 불러오기
  useEffect(() => {
    loadUsers();
    loadCompaniesAndBranches();
  }, [filters]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await UserService.getUsers(filters);
      setUsers(data || []);
    } catch (error) {
      message.error('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadCompaniesAndBranches = async () => {
    try {
      const [companiesData, branchesData] = await Promise.all([
        UserService.getCompanies(),
        UserService.getBranches()
      ]);
      setCompanies(companiesData || []);
      setBranches(branchesData || []);
      setFilteredBranches(branchesData || []);
    } catch (error) {
      logger.error('Failed to load companies and branches:', error);
    }
  };

  // 더미 데이터 제거
  const oldData = [
  ];

  const columns: ColumnsType<UserWithRelations> = [
    {
      title: '아이디',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          {text}
        </Space>
      ),
    },
    {
      title: '전화번호',
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: '회사',
      key: 'company',
      render: (_, record) => record.company?.name || '-',
    },
    {
      title: '지점',
      key: 'branch',
      render: (_, record) => record.branch?.branch_name || '-',
    },
    {
      title: '권한',
      key: 'role',
      render: (_, record) => {
        if (record.is_admin) return <Tag color="purple">슈퍼관리자</Tag>;
        if (record.has_admin_role) return <Tag color="blue">관리자</Tag>;
        return <Tag>일반</Tag>;
      },
    },
    {
      title: '등록일',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('ko-KR'),
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '활성' : '비활성'}
        </Tag>
      ),
    },
    {
      title: '관리',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            수정
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            삭제
          </Button>
          <Button
            type="link"
            icon={<LockOutlined />}
            onClick={() => handlePasswordReset(record)}
          >
            초기화
          </Button>
        </Space>
      ),
    },
  ];

  const handleEdit = (record: UserWithRelations) => {
    setEditingUser(record);
    form.setFieldsValue({
      username: record.username,
      name: record.name,
      phone_number: record.phone_number,
      company_id: record.company_id,
      branch_id: record.branch_id,
      is_active: record.is_active,
      has_admin_role: record.has_admin_role,
    });
    
    // 회사 선택에 따른 지점 필터링
    if (record.company_id) {
      const filtered = branches.filter(b => b.company_id === record.company_id);
      setFilteredBranches(filtered);
    }
    
    setModalVisible(true);
  };

  const handleDelete = (record: UserWithRelations) => {
    confirm({
      title: '사용자를 삭제하시겠습니까?',
      icon: <ExclamationCircleOutlined />,
      content: `${record.name}(${record.username}) 사용자가 삭제됩니다.`,
      async onOk() {
        try {
          await UserService.deleteUser(record.id);
          message.success('사용자가 삭제되었습니다.');
          loadUsers();
        } catch (error) {
          message.error('사용자 삭제에 실패했습니다.');
        }
      },
    });
  };

  const handlePasswordReset = (record: UserWithRelations) => {
    setEditingUser(record);
    setPasswordModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingUser) {
        // 수정
        await UserService.updateUser(editingUser.id, {
          name: values.name,
          companyId: values.company_id,
          branchId: values.branch_id,
          phoneNumber: values.phone_number,
          isActive: values.is_active,
          hasAdminRole: values.has_admin_role,
        });
        message.success('사용자 정보가 수정되었습니다.');
      } else {
        // 생성
        await UserService.createUser({
          username: values.username,
          name: values.name,
          password: values.password,
          companyId: values.company_id,
          branchId: values.branch_id,
          phoneNumber: values.phone_number,
          hasAdminRole: values.has_admin_role,
        });
        message.success('새 사용자가 등록되었습니다.');
      }
      
      setModalVisible(false);
      form.resetFields();
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      message.error(editingUser ? '사용자 수정에 실패했습니다.' : '사용자 등록에 실패했습니다.');
    }
  };

  const handlePasswordModalOk = async () => {
    try {
      await UserService.resetPassword(editingUser!.id);
      message.success('비밀번호가 초기화되었습니다.');
      setPasswordModalVisible(false);
    } catch (error) {
      message.error('비밀번호 초기화에 실패했습니다.');
    }
  };

  const handleCompanyChange = (companyId: string) => {
    form.setFieldValue('branch_id', undefined);
    const filtered = branches.filter(b => b.company_id === companyId);
    setFilteredBranches(filtered);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingUser(null);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  // 엑셀 템플릿 다운로드
  const handleTemplateDownload = () => {
    try {
      ExcelService.downloadTemplate();
      message.success('템플릿 다운로드가 완료되었습니다.');
    } catch (error) {
      message.error('템플릿 다운로드에 실패했습니다.');
    }
  };

  // 사용자 목록 엑셀 다운로드
  const handleExcelDownload = async () => {
    try {
      await ExcelService.downloadUserList(filters);
      message.success('엑셀 다운로드가 완료되었습니다.');
    } catch (error) {
      message.error('엑셀 다운로드에 실패했습니다.');
    }
  };

  // 엑셀 업로드 처리
  const handleExcelUpload = async () => {
    logger.debug('handleExcelUpload called');
    logger.debug('fileList:', fileList);
    
    if (fileList.length === 0) {
      message.error('업로드할 파일을 선택해주세요.');
      return;
    }

    const file = fileList[0];
    logger.debug('Selected file:', file);
    
    // Ant Design Upload의 File 객체 처리
    const fileToUpload = file.originFileObj || file;
    logger.debug('File to upload:', fileToUpload);
    
    if (!(fileToUpload instanceof File)) {
      message.error('파일 정보를 찾을 수 없습니다.');
      return;
    }

    setLoading(true);
    try {
      logger.debug('Calling ExcelService.uploadUserList...');
      const result = await ExcelService.uploadUserList(fileToUpload);
      logger.debug('Upload result:', result);
      
      setUploadResult(result);
      setUploadModalVisible(false);
      setUploadResultModalVisible(true);
      
      if (result.success) {
        message.success(`${result.successCount}명의 사용자가 등록되었습니다.`);
        loadUsers();
      } else {
        message.warning(`총 ${result.totalRows}건 중 ${result.successCount}건 성공, ${result.errorCount}건 실패`);
      }
      
      setFileList([]);
    } catch (error) {
      logger.error('Excel upload error:', error);
      message.error('엑셀 업로드에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 오류 내역 다운로드
  const handleErrorDownload = () => {
    if (uploadResult?.errors && uploadResult.errors.length > 0) {
      ExcelService.downloadErrorReport(uploadResult.errors);
      message.success('오류 내역 다운로드가 완료되었습니다.');
    }
  };

  return (
    <div className="user-management">
      <Breadcrumb className="breadcrumb">
        <Breadcrumb.Item>홈</Breadcrumb.Item>
        <Breadcrumb.Item>사용자 관리</Breadcrumb.Item>
      </Breadcrumb>

      <div className="page-header">
        <h1 className="page-title">사용자 관리</h1>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingUser(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            사용자 등록
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadUsers}>새로고침</Button>
          <Button icon={<UploadOutlined />} onClick={() => setUploadModalVisible(true)}>엑셀 업로드</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExcelDownload}>엑셀 다운로드</Button>
        </Space>
      </div>

      <div className="filter-section">
        <Row gutter={16} align="middle">
          <Col>
            <Select 
              allowClear
              placeholder="전체 회사" 
              style={{ width: 150 }}
              onChange={(value) => {
                setFilters({ ...filters, companyId: value, branchId: undefined });
              }}
            >
              {companies.map(company => (
                <Option key={company.id} value={company.id}>{company.name}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select 
              allowClear
              placeholder="전체 지점" 
              style={{ width: 150 }}
              value={filters.branchId}
              onChange={(value) => setFilters({ ...filters, branchId: value })}
            >
              {(filters.companyId ? branches.filter(b => b.company_id === filters.companyId) : branches).map(branch => (
                <Option key={branch.id} value={branch.id}>{branch.branch_name}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select 
              allowClear
              placeholder="전체 상태" 
              style={{ width: 120 }}
              onChange={(value) => setFilters({ ...filters, isActive: value })}
            >
              <Option value={true}>활성</Option>
              <Option value={false}>비활성</Option>
            </Select>
          </Col>
          <Col flex="auto">
            <Input.Search
              placeholder="이름, 전화번호, 아이디 검색"
              enterButton={<SearchOutlined />}
              size="middle"
              onSearch={(value) => setFilters({ ...filters, searchTerm: value || undefined })}
            />
          </Col>
        </Row>
      </div>

      {selectedRowKeys.length > 0 && (
        <div className="bulk-actions">
          <Space>
            <span>{selectedRowKeys.length}개 항목 선택됨</span>
            <Button>상태 변경</Button>
            <Button>소속 변경</Button>
            <Button danger>삭제</Button>
          </Space>
        </div>
      )}

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          total: users.length,
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `전체 ${total}명`,
        }}
      />

      <Modal
        title={editingUser ? '사용자 정보 수정' : '새 사용자 등록'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form form={form} layout="vertical">
          <h3>계정 정보</h3>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="아이디"
                name="username"
                rules={[
                  { required: !editingUser, message: '아이디를 입력해주세요' },
                  { pattern: /^[a-zA-Z0-9]+$/, message: '영문과 숫자만 입력 가능합니다' }
                ]}
              >
                <Input disabled={!!editingUser} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="비밀번호"
                name="password"
                rules={[
                  { required: !editingUser, message: '비밀번호를 입력해주세요' },
                  { min: 6, message: '비밀번호는 최소 6자 이상이어야 합니다' }
                ]}
              >
                <Input.Password disabled={!!editingUser} placeholder={editingUser ? '수정 시 비밀번호는 별도 메뉴에서 변경' : ''} />
              </Form.Item>
            </Col>
          </Row>

          <h3>기본 정보</h3>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="이름"
                name="name"
                rules={[{ required: true, message: '이름을 입력해주세요' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="전화번호"
                name="phone_number"
              >
                <Input placeholder="010-0000-0000" />
              </Form.Item>
            </Col>
          </Row>

          <h3>조직 정보</h3>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="회사" name="company_id">
                <Select 
                  placeholder="회사 선택"
                  onChange={handleCompanyChange}
                >
                  {companies.map(company => (
                    <Option key={company.id} value={company.id}>{company.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="지점" name="branch_id">
                <Select placeholder="지점 선택">
                  {filteredBranches.map(branch => (
                    <Option key={branch.id} value={branch.id}>{branch.branch_name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <h3>권한 설정</h3>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="상태" name="is_active" valuePropName="checked" initialValue={true}>
                <Checkbox>활성화</Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="관리자 권한" name="has_admin_role" valuePropName="checked">
                <Checkbox>관리자 권한 부여</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="비밀번호 초기화"
        open={passwordModalVisible}
        onOk={handlePasswordModalOk}
        onCancel={() => {
          setPasswordModalVisible(false);
        }}
        okText="초기화"
        cancelText="취소"
      >
        <div style={{ margin: '20px 0' }}>
          <p><strong>{editingUser?.name}</strong>님의 비밀번호를 초기화하시겠습니까?</p>
          <p style={{ marginTop: '10px', color: '#666' }}>
            비밀번호가 핸드폰번호(<strong>{editingUser?.username}</strong>)로 초기화됩니다.
          </p>
          <p style={{ marginTop: '10px', color: '#ff4d4f' }}>
            ※ 초기화 후 사용자는 다음 로그인 시 비밀번호를 변경해야 합니다.
          </p>
        </div>
      </Modal>

      <Modal
        title="엑셀 업로드"
        open={uploadModalVisible}
        onOk={handleExcelUpload}
        onCancel={() => {
          setUploadModalVisible(false);
          setFileList([]);
        }}
        confirmLoading={loading}
        okText="업로드"
        cancelText="취소"
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={handleTemplateDownload}
            style={{ marginBottom: 16 }}
          >
            템플릿 다운로드
          </Button>
          <div style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
            ※ 템플릿을 다운로드하여 양식에 맞게 작성 후 업로드해주세요.
          </div>
        </div>
        
        <Upload
          accept=".xlsx,.xls"
          fileList={fileList}
          beforeUpload={(file) => {
            setFileList([file]);
            return false;
          }}
          onRemove={() => {
            setFileList([]);
          }}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>파일 선택</Button>
        </Upload>
        
        <div style={{ marginTop: 16, color: '#666', fontSize: 13 }}>
          <div>• 엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.</div>
          <div>• 초기 비밀번호는 아이디(핸드폰번호)와 동일하게 설정됩니다.</div>
          <div>• 중복된 아이디는 건너뛰고 처리됩니다.</div>
        </div>
      </Modal>

      <Modal
        title="업로드 결과"
        open={uploadResultModalVisible}
        onOk={() => {
          setUploadResultModalVisible(false);
          setUploadResult(null);
        }}
        onCancel={() => {
          setUploadResultModalVisible(false);
          setUploadResult(null);
        }}
        footer={[
          <Button 
            key="download" 
            onClick={handleErrorDownload}
            disabled={!uploadResult?.errors || uploadResult.errors.length === 0}
          >
            오류 내역 다운로드
          </Button>,
          <Button 
            key="close" 
            type="primary"
            onClick={() => {
              setUploadResultModalVisible(false);
              setUploadResult(null);
            }}
          >
            확인
          </Button>
        ]}
        width={700}
      >
        {uploadResult && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div>총 {uploadResult.totalRows}건</div>
              <div style={{ color: '#52c41a' }}>성공: {uploadResult.successCount}건</div>
              <div style={{ color: '#ff4d4f' }}>실패: {uploadResult.errorCount}건</div>
            </div>
            
            {uploadResult.errors.length > 0 && (
              <div>
                <h4>오류 내역</h4>
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <th style={{ padding: 8, textAlign: 'left' }}>행</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>아이디</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>이름</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>오류 내용</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.errors.map((error, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: 8 }}>{error.row}</td>
                          <td style={{ padding: 8 }}>{error.data?.아이디}</td>
                          <td style={{ padding: 8 }}>{error.data?.이름}</td>
                          <td style={{ padding: 8, color: '#ff4d4f' }}>{error.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;